import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import { Card } from 'reactstrap';

import {
  inActiveListGet,
  listDelete,
  listStatusUpdate,
  processStatusUpdate,
  updateInActiveList,
  useInActiveList
} from '../../../store/slices/tenantSlices/configurationSlice';

import ListResetModal from './ListResetModal';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';
import { checkModulePermission, formatTableDateTime } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useDebouncedCallback } from 'use-debounce';

const InActiveList = ({
  activeTab,
  isApiCall,
  setEditRecord,
  setOpenAddModal,
  setIsEdit,
  search = undefined
}) => {
  const dispatch = useDispatch();
  const navigation = useNavigate();

  const inActiveList = useInActiveList();
  const userRolePermission = useUserRolePermission();

  const [filter, setFilter] = useState({ page: 1, limit: 10 });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [resetId, setResetId] = useState(null);

  const fetchInActiveList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter, search: search };
      await dispatch(inActiveListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteList = async () => {
    try {
      const response = await dispatch(listDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        fetchInActiveList();
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchInActiveList();
  }, [filter, activeTab, isApiCall, search]);

  const updateListStatus = async (value, id) => {
    try {
      const payload = {
        id: value?.list_id,
        list_status: value?.list_status === 'on' ? 'off' : 'on'
      };
      const response = await dispatch(listStatusUpdate(payload)).unwrap();
      if (response) {
        toast.success('Status updated successfully');
        const parentData = inActiveList?.data?.find((x) => x.process_id === id);

        if (parentData) {
          const updatedLists = parentData.lists.filter((item) => item.list_id !== value.list_id);

          const updatedInActiveList = {
            ...inActiveList,
            data: inActiveList.data
              .map((item) => (item.process_id === id ? { ...item, lists: updatedLists } : item))
              .filter((item) => item.lists.length > 0)
          };
          dispatch(updateInActiveList(updatedInActiveList));
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onStatusChange = async (val) => {
    try {
      const payload = {
        id: val.process_id,
        process_status: !val?.processStatus
      };
      const response = await dispatch(processStatusUpdate(payload)).unwrap();
      if (response) {
        toast.success('Status updated successfully');
        const updatedData = inActiveList?.data?.map((item) =>
          item.process_id === val.process_id ? { ...item, processStatus: !val.processStatus } : item
        );
        const updatedInActiveList = { ...inActiveList, data: updatedData };
        dispatch(updateInActiveList(updatedInActiveList));
      }
    } catch (error) {
      console.error('Error updating process status:', error);
      toast.error('Failed to update status');
    }
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 208px)' }}>
          <CallexLoader />
        </Card>
      ) : inActiveList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Lists', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="lists-table" style={{ height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            {inActiveList.data?.map((value, idx) => {
              return (
                <table className="report-list-table" key={idx}>
                  <thead>
                    <tr className="active-main-head-row">
                      <th colSpan="4">Process: {value?.processName || '-'}</th>
                      <th colSpan="3">Type: {value?.type || '-'}</th>
                      <th
                        colSpan="2"
                        onClick={() => onStatusChange(value)}
                        className="cursor-pointer">
                        {value?.processStatus ? (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-info">
                            <Icon.StopCircle size={16} />
                            Running
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-danger">
                            <Icon.PlayCircle size={16} />
                            Stopped
                          </div>
                        )}
                      </th>
                    </tr>
                    <tr>
                      <th style={{ width: '250px' }}>List Name</th>
                      <th style={{ width: '250px' }}>CRM Name</th>
                      <th style={{ width: '150px' }}>Pending Count</th>
                      <th style={{ width: '150px' }}>Dialed Count</th>
                      <th style={{ width: '150px' }}>Total count</th>
                      <th style={{ width: '160px' }}>Created At</th>
                      <th style={{ width: '160px' }}>Updated At</th>
                      <th style={{ width: '150px' }}>Status</th>
                      <th className="text-center" style={{ width: '120px' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {value?.lists?.map((val, index) => {
                      return (
                        <tr key={index}>
                          <td>{val?.list_name}</td>
                          <td>{val?.crm_name}</td>
                          <td>{val?.pending_count_total}</td>
                          <td>{val?.dialed_count}</td>
                          <td>{val?.total_count}</td>
                          <td>{formatTableDateTime(val?.created_at)}</td>
                          <td>{formatTableDateTime(val?.updated_at)}</td>
                          <td
                            onClick={() => updateListStatus(val, value?.process_id)}
                            style={{
                              cursor: 'pointer',
                              background: val?.list_status === 'on' ? '#2cabe333' : '#ff505033'
                            }}>
                            {val?.list_status === 'on' ? (
                              <div className="d-flex align-items-center gap-2 justify-content-center text-info">
                                <Icon.PlayCircle size={16} />
                                Active
                              </div>
                            ) : (
                              <div className="d-flex align-items-center gap-2 justify-content-center text-danger">
                                <Icon.StopCircle size={16} />
                                Inactive
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex justify-content-around align-items-center">
                              <div className="cursor-pointer text-primary">
                                <Icon.Upload
                                  size={16}
                                  onClick={() => navigation(`/configuration/lists/${val?.list_id}`)}
                                />
                              </div>
                              <div className="cursor-pointer text-violet">
                                <Icon.RotateCcw
                                  size={16}
                                  onClick={() => setResetId(val?.list_id)}
                                />
                              </div>
                              <div
                                className={`cursor-pointer text-info ${
                                  !checkModulePermission('is_edit', 'Lists', userRolePermission)
                                    ? 'callex-disabled'
                                    : ''
                                }`}>
                                <Icon.Edit
                                  size={16}
                                  onClick={() => {
                                    if (
                                      checkModulePermission('is_edit', 'Lists', userRolePermission)
                                    ) {
                                      setEditRecord(val);
                                      setIsEdit(true);
                                      setOpenAddModal(true);
                                    }
                                  }}
                                />
                              </div>
                              <div
                                className={`cursor-pointer text-danger ${
                                  !checkModulePermission('is_delete', 'Lists', userRolePermission)
                                    ? 'callex-disabled'
                                    : ''
                                }`}>
                                <Icon.Trash
                                  size={16}
                                  onClick={() => {
                                    if (
                                      checkModulePermission(
                                        'is_delete',
                                        'Lists',
                                        userRolePermission
                                      )
                                    ) {
                                      setIsDeleteId(val?.list_id);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })}
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={inActiveList?.total}
              limit={inActiveList?.limit}
              page={inActiveList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchInActiveList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 208px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteList()}
      />
      <ListResetModal
        isOpen={!!resetId}
        resetId={resetId}
        onCancelClick={(refetch = false) => {
          setResetId(null);
          fetchInActiveList();
        }}
      />
    </React.Fragment>
  );
};

InActiveList.propTypes = {
  search: PropTypes.any,
  activeTab: PropTypes.string,
  isApiCall: PropTypes.bool,
  setEditRecord: PropTypes.func,
  setOpenAddModal: PropTypes.func,
  setIsEdit: PropTypes.func
};
export default InActiveList;
